/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/* eslint-disable @elastic/eui/prefer-css-attributes-for-eui-components */

import React from 'react'
import { css } from '@emotion/react'

import {
  EuiCheckableCard,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiListGroup,
  EuiListGroupItem,
  EuiSpacer,
  EuiSwitch,
  EuiText,
  EuiTitle,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { SecurityProductTier, SecurityProductType } from '@modules/project-user-api/v1/types'
import { includesProductType } from '@modules/project-lib/securityProjectTypes'
import { useSearchAILakeAvailable } from '@modules/project-lib/hooks/useSearchAILakeAvailable'

import { ComplementProductBadge, ElasticProductBadge } from './ProductBadge'
import { getProductDefinition, PRODUCT_TYPES } from './ProductDefinition'

import type { ProductDefinition } from './ProductDefinition'

export const SecurityProductTypeCheckableCard: React.FC<{
  htmlId: string
  productTypes: SecurityProductType[]
  onProductTierChanged: (newProductTier: SecurityProductTier) => void
  productDefinition: ProductDefinition
  children?: React.ReactNode
}> = ({
  htmlId,
  productTypes,
  onProductTierChanged,
  productDefinition: { obj, title, preamble, bullets },
  children,
}) => {
  const { euiTheme } = useEuiTheme()
  return (
    <EuiCheckableCard
      id={`${htmlId}-${obj.product_line}-${obj.product_tier}`}
      css={css`min-height: 10rem; flex-grow:1;}`} /** flex-grow:1 is required to make the AIforSOC card grow to the same size as the other two cards in the project creation page */
      checkableType='radio'
      checked={includesProductType(productTypes, obj)}
      onChange={() => {
        onProductTierChanged(obj.product_tier)
      }}
      label={
        <EuiFlexGroup direction='column' gutterSize='none' style={{ height: '100%' }}>
          <EuiFlexItem
            grow={false}
            style={{
              minHeight: '236px' /*This is the longest preamble among the three cards*/,
              alignItems: 'flex-start',
            }}
          >
            {obj.product_tier === 'search_ai_lake' ? (
              <ComplementProductBadge />
            ) : (
              <ElasticProductBadge />
            )}
            <EuiSpacer size='xs' />
            <EuiText
              style={{
                whiteSpace: 'nowrap',
                lineHeight: euiTheme.size.l,
                fontWeight: euiTheme.font.weight.bold,
              }}
              size='s'
            >
              <strong
                data-test-subj={`${obj.product_line}-${obj.product_tier}-title`}
                style={{ whiteSpace: 'nowrap' }}
              >
                {title}
              </strong>
            </EuiText>
            <EuiSpacer size='xs' />
            <EuiListGroup gutterSize='none' flush={true} wrapText={true} size='xs'>
              {preamble && <EuiText size='xs'>{preamble}</EuiText>}
              {bullets.map((label, id) => (
                <div key={`${obj.product_line}-${obj.product_tier}-${id}`}>
                  <EuiSpacer size={id === 0 ? 's' : 'xs'} />
                  <EuiListGroupItem
                    size='xs'
                    style={{ paddingLeft: 0 }}
                    iconType='dot'
                    label={<EuiText size='xs'>{label}</EuiText>}
                    wrapText={true}
                  />
                </div>
              ))}
            </EuiListGroup>
          </EuiFlexItem>
          <EuiFlexItem grow={true}>
            {children && (
              <React.Fragment>
                <EuiSpacer size='m' />
                <EuiHorizontalRule margin='xs' />
              </React.Fragment>
            )}
            {children}
          </EuiFlexItem>
        </EuiFlexGroup>
      }
    />
  )
}

export const SecurityAddOnOption: React.FC<{
  htmlId: string
  productTypes: SecurityProductType[]
  onAddOnToggled: (productType: SecurityProductType) => void
  productDefinition: ProductDefinition
}> = ({
  htmlId,
  productTypes,
  onAddOnToggled,
  productDefinition: { obj, title, preamble, bullets },
}) => {
  const { euiTheme } = useEuiTheme()

  return (
    <div style={{ minHeight: '10rem' }}>
      <EuiFlexGroup gutterSize='s'>
        <EuiFlexItem grow={false}>
          <EuiSwitch
            id={`${htmlId}-${obj.product_line}-${obj.product_tier}`}
            label=''
            showLabel={false}
            disabled={
              !includesProductType(
                productTypes,
                getProductDefinition(obj.product_tier, 'security').obj,
              )
            }
            checked={includesProductType(productTypes, obj)}
            onChange={() => onAddOnToggled(obj)}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={true}>
          <EuiText
            size='s'
            style={{
              fontWeight: `${euiTheme.font.weight.bold}`,
              lineHeight: `${euiTheme.base * 1.25}px`,
            }}
          >
            <strong>
              <label htmlFor={`${htmlId}-${obj.product_line}-${obj.product_tier}`}>{title}</label>
            </strong>
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='s' />
      <EuiListGroup gutterSize='none' flush={true} wrapText={true} size='s'>
        {preamble && (
          <EuiText
            size='xs'
            css={css`
              padding-left: 0;
              line-height: ${euiTheme.base * 1.25}px;
              font-size: 12.25px;
            `}
          >
            {preamble}
          </EuiText>
        )}
        {bullets.map((label, i) => (
          <div key={`${obj.product_line}-${obj.product_tier}-bullet-${i}`}>
            <EuiSpacer size={i === 0 ? 's' : 'xs'} />
            <EuiListGroupItem
              size='xs'
              style={{ paddingLeft: 0 }}
              iconType='dot'
              label={
                <EuiText
                  size='xs'
                  style={{
                    lineHeight: `${euiTheme.base}px`,
                    fontSize: `${euiTheme.base * 0.75}px`,
                    fontWeight: euiTheme.font.weight.regular,
                  }}
                >
                  {label}
                </EuiText>
              }
              wrapText={true}
            />
          </div>
        ))}
      </EuiListGroup>
    </div>
  )
}

export const AddOnsTitle: React.FC = () => {
  const { euiTheme } = useEuiTheme()
  return (
    <EuiTitle
      css={css`
        font-size: 10.5px;
        text-transform: uppercase;
        font-weight: ${euiTheme.font.weight.bold};
        line-height: ${euiTheme.size.base};
      `}
    >
      <h4>
        <FormattedMessage
          id='project.securityProjectFeatures.addOns'
          defaultMessage='Add-on options'
        />
      </h4>
    </EuiTitle>
  )
}

const SecurityAddOnOptions: React.FC<{
  htmlId: string
  onAddOnToggled: (productType: SecurityProductType) => void
  productTypes: SecurityProductType[]
  // Only complete and essentials products have add-ons at this time
  productTier: 'complete' | 'essentials'
}> = ({ htmlId, onAddOnToggled, productTypes, productTier }) => (
  <React.Fragment>
    <EuiSpacer size='xs' />
    <AddOnsTitle />
    <EuiSpacer size='s' />
    <SecurityAddOnOption
      htmlId={htmlId}
      productTypes={productTypes}
      onAddOnToggled={onAddOnToggled}
      productDefinition={PRODUCT_TYPES[productTier].endpoint}
    />
    <EuiSpacer size='s' />
    <SecurityAddOnOption
      htmlId={htmlId}
      productTypes={productTypes}
      onAddOnToggled={onAddOnToggled}
      productDefinition={PRODUCT_TYPES[productTier].cloud}
    />
  </React.Fragment>
)

export const SecurityProductFeatureOptions: React.FC<{
  htmlId: string
  projectProductTypes: SecurityProductType[] | undefined
  productTypes: SecurityProductType[]
  onProductTierChanged: (newProductTier: SecurityProductTier) => void
  onAddOnToggled: (toggledAddOn: SecurityProductType) => void
}> = ({ htmlId, projectProductTypes, productTypes, onProductTierChanged, onAddOnToggled }) => {
  const { isSearchAILakeAvailable } = useSearchAILakeAvailable(projectProductTypes)

  return (
    <EuiFlexGroup>
      {isSearchAILakeAvailable && (
        <EuiFlexItem grow={1}>
          <SecurityProductTypeCheckableCard
            htmlId={htmlId}
            productTypes={productTypes}
            onProductTierChanged={onProductTierChanged}
            productDefinition={PRODUCT_TYPES.search_ai_lake.ai_soc}
          />
        </EuiFlexItem>
      )}
      <EuiFlexItem grow={1}>
        <SecurityProductTypeCheckableCard
          htmlId={htmlId}
          productTypes={productTypes}
          onProductTierChanged={onProductTierChanged}
          productDefinition={PRODUCT_TYPES.essentials.security}
        >
          <SecurityAddOnOptions
            htmlId={htmlId}
            productTypes={productTypes}
            onAddOnToggled={onAddOnToggled}
            productTier='essentials'
          />
        </SecurityProductTypeCheckableCard>
      </EuiFlexItem>
      <EuiFlexItem grow={1}>
        <SecurityProductTypeCheckableCard
          htmlId={htmlId}
          productTypes={productTypes}
          onProductTierChanged={onProductTierChanged}
          productDefinition={PRODUCT_TYPES.complete.security}
        >
          <SecurityAddOnOptions
            htmlId={htmlId}
            productTypes={productTypes}
            onAddOnToggled={onAddOnToggled}
            productTier='complete'
          />
        </SecurityProductTypeCheckableCard>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}
